import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import ms from 'ms';

import { type UUID } from '~/types/common';

import { sortByKey } from '@vestigas/utils';

import { queryKeysInvoiceReprocessing } from '../queryKeys';
import { type InvoiceStopperRule } from '../useQueryInvoiceStopperRules';

import { fetchInvoiceStopperRule } from './fetchInvoiceStopperRule';

/**
 * React Query based custom hook for getting the invoice stopper rule with the given stopperRuleId.
 * @param options - Optional configuration for the query.
 * @param stopperRuleId - The id of the invoice stopper rule to fetch.
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Invoice/operation/get_invoice_process_stopper_rule_vadmin_invoice_stopper_rule__stopper_rule_id__get
 */
export const useQueryInvoiceStopperRule = (
  stopperRuleId: UUID | undefined,
  options?: Omit<
    UseQueryOptions<InvoiceStopperRule, Error, InvoiceStopperRule>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery({
    enabled: Boolean(stopperRuleId),
    queryFn: async () => fetchInvoiceStopperRule(stopperRuleId),
    queryKey: queryKeysInvoiceReprocessing.getStopperRule(stopperRuleId),
    select: (data) => ({
      ...data,
      rule: {
        ...data.rule,
        checkResultStopper: sortByKey(
          data.rule.checkResultStopper,
          'checkName',
        ),
        watermarkStopper: data.rule.watermarkStopper.sort(),
      },
    }),
    staleTime: ms('30s'),
    ...options,
  });
};
